import { useCallback } from 'react'
import log from 'loglevel'
import { AuditBotPromiseClient } from '@trustero/trustero-api-web/lib/audit/auditbot_grpc_web_pb'
import { useGrpcRevalidateByMethod } from '../../useGrpcMutate'

/**
 * Refreshes the two reads that report whether a control test is outdated.
 *
 * A control test goes stale when any of five source hashes changes -- objective,
 * guidance, policies, evidence, services (see ntrced/auditbot/sources.go). The hashes are
 * recomputed server side, so an edit that moves one leaves both reads cached and the
 * Outdated pill missing until the page is reloaded.
 *
 * Deliberately separate from useInvalidateControlsCache: getAuditReadiness is one of the
 * heaviest queries in the app, so only mutations that can actually move a hash pay for it.
 *
 * It lives in its own module rather than beside useInvalidateControlsCache because callers
 * span controls and evidence, and useControls already imports from the evidence hooks.
 */
export const useInvalidateControlTestStaleness = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    try {
      await Promise.all([
        mutateFunc(AuditBotPromiseClient.prototype.getAuditControlTestRuns),
        mutateFunc(AuditBotPromiseClient.prototype.getAuditReadiness),
      ])
    } catch (error) {
      log.error('Error when invalidating control test staleness', error)
    }
  }, [mutateFunc])
}
